import { DB } from "./Cache.util";
import PrecacheIndex from "./PrecacheIndex";
import OfflineErrors from "./OfflineErrors";

// ─── Module-Level Connection Pool ──────────────────────────────
// Shared across all OfflineStoreInstance instances.
// Keyed by dbName so each catalog gets its own connection.
const _pool = new Map(); // dbName -> { conn, promise, timer }
const POOL_TIMEOUT = 300000; // 5 minutes idle timeout
export const DB_VERSION = 9;
const MAX_STORAGE_MB = 2000; // 2GB - let browser quota be the real limit
const BATCH_DELAY = 50;

/**
 * Check if an error is a QuotaExceededError (storage full).
 */
function isQuotaError(error) {
	return error instanceof DOMException && error.name === "QuotaExceededError";
}

// ─── Circuit Breaker ──────────────────────────────────────────
// Prevents infinite open/recover loops when IDB backing store is corrupted.
const _failCount = new Map(); // dbName -> consecutive open failures
const _disabled = new Set();  // databases permanently disabled this session
const MAX_OPEN_FAILURES = 3;

/**
 * Get a pooled connection for a given database name.
 * @param {string} dbName
 * @returns {Promise<IDBDatabase|null>}
 */
export async function getPooledConnection(dbName) {
	if (!dbName || _disabled.has(dbName)) return null;

	let entry = _pool.get(dbName);
	if (!entry) {
		entry = { conn: null, promise: null, timer: null };
		_pool.set(dbName, entry);
	}

	// Reset idle timer
	if (entry.timer) {
		clearTimeout(entry.timer);
	}
	entry.timer = setTimeout(() => closePoolEntry(dbName), POOL_TIMEOUT);

	// Return existing connection if valid
	if (entry.conn && entry.conn.name === dbName) {
		return entry.conn;
	}

	// Wait for pending connection
	if (entry.promise) {
		return entry.promise;
	}

	// Open new connection
	entry.promise = openConnection(dbName);
	try {
		entry.conn = await entry.promise;

		// Handle unexpected close
		if (entry.conn) {
			const db = entry.conn;
			db.onclose = () => {
				const e = _pool.get(dbName);
				if (e && e.conn === db) {
					e.conn = null;
				}
			};
			db.onerror = (e) => {
				e.preventDefault();
				console.warn("[OfflineStore] Database error:", e.target.error);
			};
		}

		return entry.conn;
	} finally {
		entry.promise = null;
	}
}

/**
 * Close and remove a pool entry.
 * @param {string} dbName
 */
export function closePoolEntry(dbName) {
	const entry = _pool.get(dbName);
	if (!entry) return;

	if (entry.timer) {
		clearTimeout(entry.timer);
		entry.timer = null;
	}
	if (entry.conn) {
		try {
			entry.conn.close();
		} catch (e) {
			// Ignore close errors
		}
		entry.conn = null;
	}
	_pool.delete(dbName);
}

/**
 * Check if IndexedDB is disabled for a given database (circuit breaker tripped).
 * @param {string} dbName
 * @returns {boolean}
 */
export function isStorageDisabled(dbName) {
	return _disabled.has(dbName);
}

/**
 * Open a new IndexedDB connection.
 * @param {string} dbName
 * @returns {Promise<IDBDatabase|null>}
 */
function openConnection(dbName) {
	return new Promise((resolve) => {
		try {
			const request = window.indexedDB.open(dbName, DB_VERSION);

			request.onerror = (event) => {
				event.preventDefault();
				event.stopPropagation();
				const count = (_failCount.get(dbName) || 0) + 1;
				_failCount.set(dbName, count);
				if (count >= MAX_OPEN_FAILURES) {
					_disabled.add(dbName);
					console.error("[OfflineStore] IndexedDB unavailable after", count, "failures — falling back to network-only mode for this session");
					OfflineErrors.log("circuit_breaker", event.target.error || new Error("IDB circuit breaker tripped"), { dbName, failCount: count });
					resolve(null);
				} else {
					console.warn(`[OfflineStore] Database open error (attempt ${count}/${MAX_OPEN_FAILURES}):`, event.target.error);
					tryRecoverDatabase(dbName).then(() => resolve(null));
				}
			};

			request.onblocked = (event) => {
				event.preventDefault();
				console.warn("[OfflineStore] Database blocked - close other tabs");
				resolve(null);
			};

			request.onsuccess = () => {
				_failCount.delete(dbName);
				resolve(request.result);
			};

			request.onupgradeneeded = (event) => {
				const db = event.target.result;
				const transaction = event.target.transaction;
				const oldVersion = event.oldVersion;

				transaction.onerror = (e) => {
					e.preventDefault();
					e.stopPropagation();
					console.warn("[OfflineStore] Upgrade transaction error:", e.target.error);
				};

				transaction.onabort = (e) => {
					e.preventDefault();
					console.warn("[OfflineStore] Upgrade transaction aborted");
				};

				try {
					// v8: Wipe stale data from pre-offline schema so fresh sync populates correctly
					if (oldVersion > 0 && oldVersion < 8) {
						for (const storeName of ["cache", "items", "responses"]) {
							if (db.objectStoreNames.contains(storeName)) {
								transaction.objectStore(storeName).clear();
							}
						}
						console.warn("[OfflineStore] Cleared stale data during upgrade from v" + oldVersion);
					}

					// v9: Drop legacy on-demand price/inventory caches. These had no
					// per-item TTL and served indefinitely-stale prices on online-only
					// catalogs (Item_Price_Fetcher / Item_Inventory_Fetcher would read
					// IDB before the API and never refetch). The fetchers are now gated
					// on offline_enabled, but pre-existing entries must be wiped so
					// users without a manual "clear site data" stop seeing stale prices.
					if (oldVersion > 0 && oldVersion < 9 && db.objectStoreNames.contains("cache")) {
						const cache = transaction.objectStore("cache");
						cache.delete("offline:prices");
						cache.delete("offline:inventory");
						console.warn("[OfflineStore] Cleared legacy on-demand price/inventory caches on upgrade to v9");
					}

					if (!db.objectStoreNames.contains("cache")) {
						db.createObjectStore("cache", { keyPath: "key" });
					}
					if (!db.objectStoreNames.contains("responses")) {
						const responses = db.createObjectStore("responses", { keyPath: "key" });
						responses.createIndex("class_name", "class_name", { unique: false });
						responses.createIndex("cached_on", "cached_on", { unique: false });
					}
					if (!db.objectStoreNames.contains("sync_queue")) {
						const queue = db.createObjectStore("sync_queue", { keyPath: "id", autoIncrement: true });
						queue.createIndex("class_name", "class_name", { unique: false });
						queue.createIndex("created_on", "created_on", { unique: false });
					}
					if (!db.objectStoreNames.contains("items")) {
						const items = db.createObjectStore("items", { keyPath: "key" });
						items.createIndex("class_name", "class_name", { unique: false });
						items.createIndex("cached_on", "cached_on", { unique: false });
						items.createIndex("group_code", "group_code", { unique: false });
					} else {
						const items = event.target.transaction.objectStore("items");
						if (!items.indexNames.contains("group_code")) {
							items.createIndex("group_code", "group_code", { unique: false });
						}
					}
				} catch (e) {
					console.warn("[OfflineStore] Error creating stores:", e);
				}
			};
		} catch (e) {
			console.warn("[OfflineStore] Failed to open database:", e);
			resolve(null);
		}
	});
}

// Module-level recovery tracking per dbName
const _recovering = new Set();

async function tryRecoverDatabase(dbName) {
	if (_recovering.has(dbName) || !dbName) return;
	_recovering.add(dbName);

	console.warn("[OfflineStore] Attempting database recovery...");
	closePoolEntry(dbName);

	try {
		await new Promise((resolve) => {
			const deleteRequest = window.indexedDB.deleteDatabase(dbName);
			deleteRequest.onsuccess = () => resolve();
			deleteRequest.onerror = () => {
				console.warn("[OfflineStore] Could not delete database");
				resolve();
			};
			deleteRequest.onblocked = () => {
				console.warn("[OfflineStore] Database deletion blocked - close other tabs");
				resolve();
			};
		});
	} catch (e) {
		console.warn("[OfflineStore] Recovery failed:", e);
	}

	_recovering.delete(dbName);
}

// ─── Levenshtein (pure function) ─────────────────────────────────

/**
 * Calculate Levenshtein distance between two strings.
 * Uses single-row optimization (O(n) space) with early termination.
 * @param {string} a
 * @param {string} b
 * @param {number} [maxDistance=Infinity]
 * @returns {number}
 */
function levenshteinDistance(a, b, maxDistance = Infinity) {
	if (a.length === 0) return b.length;
	if (b.length === 0) return a.length;

	const aLen = a.length;
	const bLen = b.length;
	let prevRow = new Array(aLen + 1);

	for (let j = 0;j <= aLen;j++) {
		prevRow[j] = j;
	}

	for (let i = 1;i <= bLen;i++) {
		const currRow = new Array(aLen + 1);
		currRow[0] = i;
		let rowMin = i;

		for (let j = 1;j <= aLen;j++) {
			if (b.charAt(i - 1) === a.charAt(j - 1)) {
				currRow[j] = prevRow[j - 1];
			} else {
				currRow[j] = Math.min(
					prevRow[j - 1] + 1,
					currRow[j - 1] + 1,
					prevRow[j] + 1
				);
			}
			if (currRow[j] < rowMin) rowMin = currRow[j];
		}

		if (rowMin > maxDistance) return rowMin;

		prevRow = currRow;
	}

	return prevRow[aLen];
}


// ═══════════════════════════════════════════════════════════════════
// OfflineStoreInstance — per-catalog instance with all catalog-dependent methods
// ═══════════════════════════════════════════════════════════════════

class OfflineStoreInstance {
	#dbName;
	#catalogId;
	#pendingWrites = [];
	#writeTimer = null;
	#lastSyncTimes = {};
	#writeCounter = 0;

	constructor(catalogId) {
		this.#catalogId = catalogId;
		this.#dbName = DB.Make_Name(catalogId);
	}

	get catalogId() { return this.#catalogId; }
	get dbName() { return this.#dbName; }
	get isAvailable() { return !_disabled.has(this.#dbName); }

	/**
	 * Get current catalog ID.
	 * @returns {string|null}
	 */
	GetCurrentCatalogId() {
		return String(this.#catalogId);
	}

	// ─── Batch Writes ──────────────────────────────────────────

	#queueWrite(storeName, operation, data) {
		return new Promise((resolve, reject) => {
			this.#pendingWrites.push({ storeName, operation, data, resolve, reject });

			if (this.#writeTimer) {
				clearTimeout(this.#writeTimer);
			}

			if (this.#pendingWrites.length >= 50) {
				this.#flushWrites();
			} else {
				this.#writeTimer = setTimeout(() => this.#flushWrites(), BATCH_DELAY);
			}
		});
	}

	async #flushWrites() {
		if (this.#writeTimer) {
			clearTimeout(this.#writeTimer);
			this.#writeTimer = null;
		}

		if (this.#pendingWrites.length === 0) return;

		const writes = this.#pendingWrites;
		this.#pendingWrites = [];

		const db = await getPooledConnection(this.#dbName);
		if (!db) {
			writes.forEach(w => w.resolve());
			return;
		}

		const byStore = {};
		for (const write of writes) {
			if (!byStore[write.storeName]) byStore[write.storeName] = [];
			byStore[write.storeName].push(write);
		}

		for (const [storeName, storeWrites] of Object.entries(byStore)) {
			try {
				if (!db.objectStoreNames.contains(storeName)) {
					storeWrites.forEach(w => w.resolve());
					continue;
				}

				const tx = db.transaction([storeName], "readwrite");
				const store = tx.objectStore(storeName);

				// Collect writes that should resolve on tx.oncomplete (not immediately)
				const deferredWrites = [];

				for (const write of storeWrites) {
					try {
						if (write.operation === "put") {
							store.put(write.data);
							deferredWrites.push(write);
						} else if (write.operation === "delete") {
							store.delete(write.data);
							deferredWrites.push(write);
						} else if (write.operation === "add") {
							// add needs to resolve with the auto-generated ID
							const request = store.add(write.data);
							request.onsuccess = () => write.resolve(request.result);
							request.onerror = (e) => {
								if (isQuotaError(request.error)) {
									e.preventDefault();
									OfflineErrors.log("QuotaExceeded", request.error, { storeName, operation: "add" });
									write.resolve(null);
									this.#evictAndRetry();
								} else {
									write.reject(request.error);
								}
							};
						}
					} catch (e) {
						write.reject(e);
					}
				}

				await new Promise((resolve) => {
					tx.oncomplete = () => {
						deferredWrites.forEach(w => {
							try { w.resolve(); } catch (_) { /* already settled */ }
						});
						resolve();
					};
					tx.onerror = (e) => {
						const txError = e.target?.error;
						if (isQuotaError(txError)) {
							e.preventDefault();
							console.warn(`[OfflineStore] QuotaExceededError in ${storeName} — writes skipped, triggering eviction`);
							OfflineErrors.log("QuotaExceeded", txError, { storeName, writeCount: deferredWrites.length });
							deferredWrites.forEach(w => {
								try { w.resolve(); } catch (_) { /* already settled */ }
							});
							this.#evictAndRetry();
						} else {
							console.warn(`[OfflineStore] Transaction failed for ${storeName}:`, txError);
							deferredWrites.forEach(w => {
								try { w.reject(txError || new Error("Transaction failed")); } catch (_) { /* already settled */ }
							});
						}
						resolve();
					};
					tx.onabort = (e) => {
						const txError = e.target?.error;
						if (isQuotaError(txError)) {
							e.preventDefault();
							console.warn(`[OfflineStore] QuotaExceededError (abort) in ${storeName} — writes skipped, triggering eviction`);
							OfflineErrors.log("QuotaExceeded", txError, { storeName, writeCount: deferredWrites.length, abort: true });
							deferredWrites.forEach(w => {
								try { w.resolve(); } catch (_) { /* already settled */ }
							});
							this.#evictAndRetry();
						} else {
							console.warn(`[OfflineStore] Transaction aborted for ${storeName}:`, txError);
							deferredWrites.forEach(w => {
								try { w.reject(txError || new Error("Transaction aborted")); } catch (_) { /* already settled */ }
							});
						}
						resolve();
					};
				});
			} catch (e) {
				if (isQuotaError(e)) {
					OfflineErrors.log("QuotaExceeded", e, { storeName, phase: "txSetup" });
					storeWrites.forEach(w => {
						try { w.resolve(); } catch (_) { /* already settled */ }
					});
					this.#evictAndRetry();
				} else {
					storeWrites.forEach(w => {
						try { w.reject(e); } catch (_) { /* already settled */ }
					});
				}
			}
		}
	}

	// ─── LRU Cache Eviction ────────────────────────────────────

	/**
	 * Aggressive eviction triggered by QuotaExceededError.
	 * More aggressive than periodic #checkAndEvict() — 500 responses + 2000 items.
	 * Fire-and-forget; does not block the caller.
	 */
	#evictAndRetry() {
		if (this._evicting) return;
		this._evicting = true;

		Promise.all([
			this.#evictOldest("responses", 500),
			this.#evictOldest("items", 2000),
		]).then(() => {
			this._evicting = false;
		}).catch(() => {
			this._evicting = false;
		});
	}

	async #checkAndEvict() {
		try {
			if (!navigator.storage?.estimate) return;

			const { usage, quota } = await navigator.storage.estimate();
			const usageMB = (usage || 0) / (1024 * 1024);
			const quotaMB = (quota || 0) / (1024 * 1024);

			if (usageMB > MAX_STORAGE_MB || usageMB > quotaMB * 0.8) {
				await this.#evictOldest("responses", 100);
				await this.#evictOldest("items", 500);
			}
		} catch (e) {
			// Ignore storage estimation errors
		}
	}

	async #evictOldest(storeName, count) {
		const db = await getPooledConnection(this.#dbName);
		if (!db || !db.objectStoreNames.contains(storeName)) return;

		try {
			const tx = db.transaction([storeName], "readwrite");
			const store = tx.objectStore(storeName);
			const index = store.index("cached_on");
			const request = index.openCursor();
			let evicted = 0;

			request.onsuccess = (event) => {
				const cursor = event.target.result;
				if (cursor && evicted < count) {
					cursor.delete();
					evicted++;
					cursor.continue();
				}
			};

			await new Promise((resolve) => {
				tx.oncomplete = resolve;
				tx.onerror = () => resolve();
			});
		} catch (e) {
			console.warn("[OfflineStore] Eviction failed:", e);
		}
	}

	// ─── Delta Updates ─────────────────────────────────────────

	GetLastSyncTime(className) {
		return this.#lastSyncTimes[className] || null;
	}

	SetLastSyncTime(className) {
		this.#lastSyncTimes[className] = Date.now();
	}

	GetDeltaParams(className) {
		const lastSync = this.#lastSyncTimes[className];
		if (!lastSync) return null;
		return { since: new Date(lastSync).toISOString() };
	}

	// ─── Response Cache ─────────────────────────────────────────

	async CacheResponse(key, className, responseData) {
		// Sanitize: strip functions and non-serializable properties.
		// Model objects may carry methods (e.g. updateData) that IndexedDB cannot clone.
		let sanitizedData;
		try {
			sanitizedData = JSON.parse(JSON.stringify(responseData));
		} catch (e) {
			console.warn("[OfflineStore] Response data is not serializable, skipping cache:", e);
			OfflineErrors.log("CacheResponse", e, { key });
			return;
		}

		await this.#queueWrite("responses", "put", {
			key,
			class_name: className,
			response_data: sanitizedData,
			cached_on: Date.now(),
		});

		this.#writeCounter++;
		if (this.#writeCounter % 10 === 0) {
			await this.#checkAndEvict();
		}
	}

	GetCachedResponse(key, ttlMs = undefined) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve();
				if (!db.objectStoreNames.contains("responses")) return resolve();

				const tx = db.transaction(["responses"], "readonly");
				const store = tx.objectStore("responses");
				const request = store.get(key);

				request.onsuccess = () => {
					const record = request.result;
					if (!record) return resolve();
					if (ttlMs && (Date.now() - record.cached_on > ttlMs)) {
						return resolve();
					}
					resolve(record.response_data);
				};
				request.onerror = () => resolve();
			}).catch((e) => {
				OfflineErrors.log("GetCachedResponse", e, { key });
				resolve();
			});
		});
	}

	GetCachedResponseWithMeta(key, ttlMs) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("responses")) return resolve(null);

				const tx = db.transaction(["responses"], "readonly");
				const store = tx.objectStore("responses");
				const request = store.get(key);

				request.onsuccess = () => {
					const record = request.result;
					if (!record) return resolve(null);
					const age = Date.now() - record.cached_on;
					resolve({
						data: record.response_data,
						cachedOn: record.cached_on,
						isStale: ttlMs ? age > ttlMs : false,
					});
				};
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetCachedResponseWithMeta", e, { key });
				resolve(null);
			});
		});
	}

	GetCachedGalleryAttributes() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("responses")) return resolve(null);

				const tx = db.transaction(["responses"], "readonly");
				const store = tx.objectStore("responses");
				const request = store.openCursor();

				request.onsuccess = (event) => {
					const cursor = event.target.result;
					if (cursor) {
						const key = cursor.key;
						if (typeof key === 'string' &&
							key.includes('Catalog_Page/') &&
							key.includes('/attributes:') &&
							!key.includes('/attributes/details')) {
							const record = cursor.value;
							if (record?.response_data && Array.isArray(record.response_data)) {
								return resolve(record.response_data);
							}
						}
						cursor.continue();
					} else {
						resolve(null);
					}
				};
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetCachedGalleryAttributes", e);
				resolve(null);
			});
		});
	}

	GetCacheSummary() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve([]);
				if (!db.objectStoreNames.contains("responses")) return resolve([]);

				const tx = db.transaction(["responses"], "readonly");
				const store = tx.objectStore("responses");
				const request = store.getAll();

				request.onsuccess = () => {
					const records = request.result || [];
					const grouped = {};
					for (const record of records) {
						const cls = record.class_name || "unknown";
						if (!grouped[cls]) {
							grouped[cls] = { class_name: cls, count: 0, oldest: Infinity, newest: 0 };
						}
						grouped[cls].count++;
						grouped[cls].oldest = Math.min(grouped[cls].oldest, record.cached_on);
						grouped[cls].newest = Math.max(grouped[cls].newest, record.cached_on);
					}
					resolve(Object.values(grouped).sort((a, b) => b.count - a.count));
				};
				request.onerror = () => resolve([]);
			}).catch((e) => {
				OfflineErrors.log("GetCacheSummary", e);
				resolve([]);
			});
		});
	}

	async ClearClassCache(className) {
		const db = await getPooledConnection(this.#dbName);
		if (!db || !db.objectStoreNames.contains("responses")) return;

		const tx = db.transaction(["responses"], "readwrite");
		const store = tx.objectStore("responses");
		const index = store.index("class_name");
		const request = index.openCursor(IDBKeyRange.only(className));

		request.onsuccess = (event) => {
			const cursor = event.target.result;
			if (cursor) {
				cursor.delete();
				cursor.continue();
			}
		};

		await new Promise((resolve) => {
			tx.oncomplete = resolve;
			tx.onerror = () => resolve();
			tx.onabort = () => resolve();
		});
	}

	// ─── Sync Queue ─────────────────────────────────────────────

	QueueMutation(className, method, url, payload, config) {
		// Sanitize payload: strip functions/non-serializable properties
		let sanitizedPayload = payload;
		if (payload) {
			try {
				sanitizedPayload = JSON.parse(JSON.stringify(payload));
			} catch (e) {
				console.warn("[OfflineStore] Payload is not serializable, cannot queue mutation:", e);
				return Promise.reject(e);
			}
		}

		return this.#queueWrite("sync_queue", "add", {
			class_name: className,
			method,
			url,
			payload: sanitizedPayload,
			config: { headers: config?.headers, params: config?.params },
			created_on: Date.now(),
			status: "pending",
		});
	}

	GetPendingMutations() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve([]);
				if (!db.objectStoreNames.contains("sync_queue")) return resolve([]);

				const tx = db.transaction(["sync_queue"], "readonly");
				const store = tx.objectStore("sync_queue");
				const index = store.index("created_on");
				const request = index.getAll();

				request.onsuccess = () => {
					const results = (request.result || [])
						.filter(entry => entry.status === "pending");
					resolve(results);
				};
				request.onerror = () => resolve([]);
			}).catch((e) => {
				OfflineErrors.log("GetPendingMutations", e);
				resolve([]);
			});
		});
	}

	async ClearMutation(id) {
		await this.#queueWrite("sync_queue", "delete", id);
	}

	async FailMutation(id) {
		const db = await getPooledConnection(this.#dbName);
		if (!db || !db.objectStoreNames.contains("sync_queue")) return;

		const tx = db.transaction(["sync_queue"], "readwrite");
		const store = tx.objectStore("sync_queue");
		const request = store.get(id);

		request.onsuccess = () => {
			const record = request.result;
			if (record) {
				record.status = "failed";
				store.put(record);
			}
		};

		await new Promise((resolve) => {
			tx.oncomplete = resolve;
			tx.onerror = () => resolve();
			tx.onabort = () => resolve();
		});
	}

	GetPendingCount() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(0);
				if (!db.objectStoreNames.contains("sync_queue")) return resolve(0);

				const tx = db.transaction(["sync_queue"], "readonly");
				const store = tx.objectStore("sync_queue");
				const request = store.getAll();

				request.onsuccess = () => {
					const count = (request.result || [])
						.filter(entry => entry.status === "pending").length;
					resolve(count);
				};
				request.onerror = () => resolve(0);
			}).catch((e) => {
				OfflineErrors.log("GetPendingCount", e);
				resolve(0);
			});
		});
	}

	async ClearQueue() {
		const db = await getPooledConnection(this.#dbName);
		if (!db || !db.objectStoreNames.contains("sync_queue")) return;

		const tx = db.transaction(["sync_queue"], "readwrite");
		const store = tx.objectStore("sync_queue");
		store.clear();
	}

	// ─── Item-Level Cache ──────────────────────────────────────

	async CacheItems(className, items) {
		if (!Array.isArray(items) || items.length === 0) return;

		const now = Date.now();
		const writes = [];

		for (const item of items) {
			if (!item?.id) continue;

			// Strip functions/non-serializable properties from model objects
			let sanitizedItem;
			try {
				sanitizedItem = JSON.parse(JSON.stringify(item));
			} catch (e) {
				continue; // Skip un-serializable items
			}

			const key = `${className}:${sanitizedItem.id}`;
			writes.push(this.#queueWrite("items", "put", {
				key,
				class_name: className,
				item_id: sanitizedItem.id,
				item_data: sanitizedItem,
				cached_on: now,
			}));
		}

		await Promise.allSettled(writes);
	}

	GetItemsByIds(className, ids) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve([]);
				if (!db.objectStoreNames.contains("items")) return resolve([]);

				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");
				const results = [];

				if (ids.length === 0) return resolve([]);

				let completed = 0;
				for (const id of ids) {
					const key = `${className}:${id}`;
					const request = store.get(key);
					request.onsuccess = () => {
						if (request.result?.item_data) {
							results.push(request.result.item_data);
						}
						completed++;
						if (completed === ids.length) {
							resolve(results);
						}
					};
					request.onerror = () => {
						completed++;
						if (completed === ids.length) {
							resolve(results);
						}
					};
				}
			}).catch((e) => {
				OfflineErrors.log("GetItemsByIds", e, { className });
				resolve([]);
			});
		});
	}

	GetCachedItems(className, limit = 60, offset = 0) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve({ items: [], total: 0 });
				if (!db.objectStoreNames.contains("items")) return resolve({ items: [], total: 0 });

				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");
				const index = store.index("class_name");
				const request = index.getAll(IDBKeyRange.only(className));

				request.onsuccess = () => {
					const records = request.result || [];
					records.sort((a, b) => b.cached_on - a.cached_on);
					const total = records.length;
					const items = records
						.slice(offset, offset + limit)
						.map(r => r.item_data);
					resolve({ items, total });
				};
				request.onerror = () => resolve({ items: [], total: 0 });
			}).catch((e) => {
				OfflineErrors.log("GetCachedItems", e, { className });
				resolve({ items: [], total: 0 });
			});
		});
	}

	GetItemCount(className) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(0);
				if (!db.objectStoreNames.contains("items")) return resolve(0);

				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");
				const index = store.index("class_name");
				const request = index.count(IDBKeyRange.only(className));

				request.onsuccess = () => resolve(request.result || 0);
				request.onerror = () => resolve(0);
			}).catch((e) => {
				OfflineErrors.log("GetItemCount", e, { className });
				resolve(0);
			});
		});
	}

	// ─── Database Management ────────────────────────────────────

	async ResetDatabase() {
		closePoolEntry(this.#dbName);

		try {
			await new Promise((resolve) => {
				const deleteRequest = window.indexedDB.deleteDatabase(this.#dbName);
				deleteRequest.onsuccess = resolve;
				deleteRequest.onerror = () => resolve();
				deleteRequest.onblocked = () => resolve();
			});
			return true;
		} catch (e) {
			console.warn("[OfflineStore] Reset failed:", e);
			return false;
		}
	}

	// ─── Precache / Offline Sync ────────────────────────────────

	async CachePrecacheItems(items) {
		if (!Array.isArray(items) || items.length === 0) return;

		const now = Date.now();
		const writes = [];

		for (const item of items) {
			const itemId = item?.attribs?.id;
			if (!itemId) continue;

			// Strip functions/non-serializable properties from model objects
			let sanitizedItem;
			try {
				sanitizedItem = JSON.parse(JSON.stringify(item));
			} catch (e) {
				continue; // Skip un-serializable items
			}

			const key = `precache:${itemId}`;
			const sequence = parseInt(sanitizedItem?.attribs?.sequence, 10) || 0;
			writes.push(this.#queueWrite("items", "put", {
				key,
				class_name: "PrecacheItem",
				item_id: itemId,
				sequence,
				group_code: sanitizedItem.group || null,
				item_data: sanitizedItem,
				cached_on: now,
			}));
		}

		await Promise.allSettled(writes);
	}

	GetPrecachedItems(limit = 0, offset = 0) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve({ items: [], total: 0 });
				if (!db.objectStoreNames.contains("items")) return resolve({ items: [], total: 0 });

				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");
				const index = store.index("class_name");
				const request = index.getAll(IDBKeyRange.only("PrecacheItem"));

				request.onsuccess = () => {
					const records = request.result || [];
					const total = records.length;

					records.sort((a, b) => {
						const seqA = a.sequence || 0;
						const seqB = b.sequence || 0;
						if (seqA > 0 && seqB > 0) return seqA - seqB;
						if (seqA > 0 && seqB === 0) return -1;
						if (seqA === 0 && seqB > 0) return 1;
						return a.item_id - b.item_id;
					});

					const mapRecord = (r) => {
						const item = r.item_data;
						if (r.group_code && !item.group) {
							item.group = r.group_code;
						}
						return item;
					};

					const items = limit > 0
						? records.slice(offset, offset + limit).map(mapRecord)
						: records.slice(offset).map(mapRecord);

					resolve({ items, total });
				};
				request.onerror = () => resolve({ items: [], total: 0 });
			}).catch((e) => {
				OfflineErrors.log("GetPrecachedItems", e);
				resolve({ items: [], total: 0 });
			});
		});
	}

	DebugPrecacheItemIds(_targetId) {
		return Promise.resolve();
	}

	GetPrecacheItemCount() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(0);
				if (!db.objectStoreNames.contains("items")) return resolve(0);

				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");
				const index = store.index("class_name");
				const request = index.count(IDBKeyRange.only("PrecacheItem"));

				request.onsuccess = () => resolve(request.result || 0);
				request.onerror = () => resolve(0);
			}).catch((e) => {
				OfflineErrors.log("GetPrecacheItemCount", e);
				resolve(0);
			});
		});
	}

	GetPrecachedItemById(itemId) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("items")) return resolve(null);

				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");
				const key = `precache:${itemId}`;
				const request = store.get(key);

				request.onsuccess = () => {
					const result = request.result?.item_data;
					resolve(result || null);
				};
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetPrecachedItemById", e, { itemId });
				resolve(null);
			});
		});
	}

	async GetPrecachedItemsByIds(itemIds) {
		if (!itemIds || itemIds.length === 0) return [];

		try {
			const db = await getPooledConnection(this.#dbName);
			if (!db || !db.objectStoreNames.contains("items")) {
				return [];
			}

			const BATCH_SIZE = 500;
			const results = [];

			// Batch reads to avoid overwhelming IDB with 250k+ concurrent gets
			for (let i = 0; i < itemIds.length; i += BATCH_SIZE) {
				const batchIds = itemIds.slice(i, i + BATCH_SIZE);
				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");

				const batchResults = await new Promise((resolve) => {
					const batch = [];
					let completed = 0;

					for (const id of batchIds) {
						const request = store.get(`precache:${id}`);
						request.onsuccess = () => {
							if (request.result?.item_data) {
								batch.push(request.result.item_data);
							}
							if (++completed === batchIds.length) resolve(batch);
						};
						request.onerror = () => {
							if (++completed === batchIds.length) resolve(batch);
						};
					}
				});

				results.push(...batchResults);
			}

			return results;
		} catch (e) {
			OfflineErrors.log("GetPrecachedItemsByIds", e, { count: itemIds?.length });
			return [];
		}
	}

	/**
	 * Iterate over all PrecacheItems using a cursor, calling callback for each.
	 * Never loads all items into memory at once — suitable for 250k+ catalogs.
	 * @param {Function} callback - Called with (item) for each precache item
	 * @returns {Promise<number>} Total items iterated
	 */
	async ForEachPrecacheItem(callback) {
		try {
			const db = await getPooledConnection(this.#dbName);
			if (!db || !db.objectStoreNames.contains("items")) return 0;

			return new Promise((resolve) => {
				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");
				const index = store.index("class_name");
				const request = index.openCursor(IDBKeyRange.only("PrecacheItem"));
				let count = 0;

				request.onsuccess = (event) => {
					const cursor = event.target.result;
					if (cursor) {
						const item = cursor.value.item_data;
						if (item) {
							callback(item);
							count++;
						}
						cursor.continue();
					} else {
						resolve(count);
					}
				};
				request.onerror = () => resolve(count);
			});
		} catch (e) {
			OfflineErrors.log("ForEachPrecacheItem", e);
			return 0;
		}
	}

	GetItemsByGroupCode(groupCode) {
		return new Promise((resolve) => {
			if (!groupCode) return resolve([]);

			const sizeStripRegex = /\s+(SS|S\d+|XS|XXL|XL|Small|Medium|Large|S|M|L|\d+(\.\d+)?)$/i;
			const normalizedGroupCode = String(groupCode).replace(sizeStripRegex, '').trim();

			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve([]);
				if (!db.objectStoreNames.contains("items")) return resolve([]);

				const tx = db.transaction(["items"], "readonly");
				const store = tx.objectStore("items");

				const filterByNormalizedGroup = (records) => {
					return records
						.filter(r => {
							const itemGroupCode = r.group_code || r.item_data?.group || r.item_data?.attribs?.family;
							if (!itemGroupCode) return false;
							const normalizedItemGroup = String(itemGroupCode).replace(sizeStripRegex, '').trim();
							return normalizedItemGroup === normalizedGroupCode;
						})
						.map(r => r.item_data);
				};

				if (store.indexNames.contains("group_code")) {
					const groupIndex = store.index("group_code");
					const exactRequest = groupIndex.getAll(IDBKeyRange.only(groupCode));

					exactRequest.onsuccess = () => {
						const exactResults = exactRequest.result || [];
						if (exactResults.length > 1) {
							resolve(exactResults.map(r => r.item_data));
							return;
						}

						const normalizedRequest = groupIndex.getAll(IDBKeyRange.only(normalizedGroupCode));
						normalizedRequest.onsuccess = () => {
							const normalizedResults = normalizedRequest.result || [];
							if (normalizedResults.length > 1) {
								resolve(normalizedResults.map(r => r.item_data));
								return;
							}

							if (!store.indexNames.contains("class_name")) {
								resolve(exactResults.map(r => r.item_data));
								return;
							}
							const classIndex = store.index("class_name");
							const fallbackRequest = classIndex.getAll(IDBKeyRange.only("PrecacheItem"));
							fallbackRequest.onsuccess = () => {
								resolve(filterByNormalizedGroup(fallbackRequest.result || []));
							};
							fallbackRequest.onerror = () => resolve([]);
						};
						normalizedRequest.onerror = () => resolve(exactResults.map(r => r.item_data));
					};
					exactRequest.onerror = () => resolve([]);
					return;
				}

				if (store.indexNames.contains("class_name")) {
					const index = store.index("class_name");
					const request = index.getAll(IDBKeyRange.only("PrecacheItem"));
					request.onsuccess = () => resolve(filterByNormalizedGroup(request.result || []));
					request.onerror = () => resolve([]);
				} else {
					const allItems = [];
					const cursorRequest = store.openCursor();
					cursorRequest.onsuccess = (event) => {
						const cursor = event.target.result;
						if (cursor) {
							if (cursor.value.class_name === "PrecacheItem") {
								allItems.push(cursor.value);
							}
							cursor.continue();
						} else {
							resolve(filterByNormalizedGroup(allItems));
						}
					};
					cursorRequest.onerror = () => resolve([]);
				}
			}).catch((e) => {
				OfflineErrors.log("GetItemsByGroupCode", e, { groupCode });
				resolve([]);
			});
		});
	}

	async CacheAssets(assets) {
		if (!Array.isArray(assets)) return;

		let sanitized;
		try {
			sanitized = JSON.parse(JSON.stringify(assets));
		} catch (e) {
			console.warn("[OfflineStore] Could not serialize assets:", e);
			return;
		}

		await this.#queueWrite("cache", "put", {
			key: "offline:assets",
			value: sanitized,
			cached_on: Date.now(),
		});
	}

	GetCachedAssets() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get("offline:assets");

				request.onsuccess = () => resolve(request.result?.value || null);
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetCachedAssets", e);
				resolve(null);
			});
		});
	}

	async CacheFilters(filters) {
		if (!Array.isArray(filters)) return;

		let sanitized;
		try {
			sanitized = JSON.parse(JSON.stringify(filters));
		} catch (e) {
			console.warn("[OfflineStore] Could not serialize filters:", e);
			return;
		}

		// Normalize filter structure for consistency
		for (const filter of sanitized) {
			// Precache filter_defs use "type" but the frontend expects "display_type"
			if (filter.type && !filter.display_type) {
				filter.display_type = filter.type;
			}

			// Handle backend format where id is an object: {common: 35, options: [...]}
			// Normalize id to string format (use reference if available)
			if (filter.id && typeof filter.id === 'object' && filter.id.common !== undefined) {
				// Use reference as the string ID if available, otherwise build from common
				const stringId = filter.reference || `c.${filter.id.common}`;
				filter.id = stringId;
			}
		}

		// Merge with existing cached filters instead of blindly replacing.
		// The `/filters` endpoint is frequently called with active filter params to
		// recalculate counts; those responses may omit options/filters with 0 results.
		// If we overwrite the cache with that partial response, the UI will "lose"
		// options (e.g., Rings/Necklaces disappear) until a full refresh.
		const existingFilters = await this.GetCachedFilters() || [];
		const mergedFilters = (() => {
			if (!Array.isArray(existingFilters) || existingFilters.length === 0) return sanitized;
			if (!Array.isArray(sanitized) || sanitized.length === 0) return existingFilters;

			// Prefer reference/property as canonical identity because precache filter_defs
			// often have numeric ids while the API uses string ids (e.g. "c.35").
			const byId = (f) => String(
				f?.reference ?? f?.property ?? f?.name ?? f?.id ?? f?.filter_id ?? f?.filterId ?? ''
			);

			const existingById = new Map(existingFilters.map(f => [byId(f), f]));
			const incomingById = new Map(sanitized.map(f => [byId(f), f]));

			// Keep existing order, merge in incoming counts/options.
			const merged = existingFilters.map(existing => {
				const key = byId(existing);
				const incoming = incomingById.get(key);
				if (!incoming) return existing;

				const mergedFilter = { ...existing, ...incoming };

				// If incoming lacks values, never wipe existing values.
				if (!Array.isArray(incoming.values)) {
					mergedFilter.values = existing.values;
					return mergedFilter;
				}

				// If existing lacks values, take incoming values.
				if (!Array.isArray(existing.values)) {
					mergedFilter.values = incoming.values;
					return mergedFilter;
				}

				// Merge counts into existing options without dropping options.
				const existingOptionsById = new Map(existing.values.map(v => [String(v?.id), v]));
				const incomingOptionsById = new Map(incoming.values.map(v => [String(v?.id), v]));

				const mergedValues = existing.values.map(opt => {
					const updated = incomingOptionsById.get(String(opt?.id));
					if (!updated) return opt;
					// Preserve existing label/parent/children structure unless incoming provides it.
					return { ...opt, ...updated, label: opt?.label ?? updated?.label };
				});

				// Add any new options from incoming that aren't already present.
				for (const [optId, opt] of incomingOptionsById.entries()) {
					if (!existingOptionsById.has(optId)) mergedValues.push(opt);
				}

				mergedFilter.values = mergedValues;
				return mergedFilter;
			});

			// Add brand new filters from incoming.
			for (const [key, incoming] of incomingById.entries()) {
				if (key && !existingById.has(key)) merged.push(incoming);
			}

			return merged;
		})();

		await this.#queueWrite("cache", "put", {
			key: "offline:filters",
			value: mergedFilters,
			cached_on: Date.now(),
		});

		// Build referenceMap: maps between the three ID systems used for filters:
		//   1. reference  (e.g. "material-flat") — used by frontend selectedFilters
		//   2. idStr      (e.g. "10801603")      — filter definition ID from /filters response
		//   3. commonStr  (e.g. "3")             — Common_Filter ID, used as key in item.filters
		// The filter index (PrecacheIndex) is keyed by commonStr because that's what
		// items store in their .filters object. The /filters endpoint does NOT include
		// the common filter ID, but the /items/meta endpoint does (stored as meta_filters
		// during sync). We merge both sources to build the complete mapping.
		const existingMap = await this.GetFilterReferenceMap() || {};
		const referenceMap = { ...existingMap };

		// Load meta_filters from sync (has common IDs) to enrich the mapping
		const metaFilters = await this.GetSyncMetadata("meta_filters");
		const commonByReference = {};
		if (Array.isArray(metaFilters)) {
			for (const mf of metaFilters) {
				const ref = mf.reference;
				const common = mf.common;
				if (ref && common != null) {
					commonByReference[ref] = String(common);
				}
			}
		}

		for (const filter of mergedFilters) {
			const rawId = filter.id || filter.filter_id || filter.filterId;
			const reference = filter.reference || filter.ref || filter.property || filter.name;
			// common may come from the filter itself (unlikely from /filters) or from meta_filters
			const commonId = filter.common ?? filter.common_id ?? (reference ? commonByReference[reference] : undefined);
			if (!reference) continue;

			// Normalize the filter definition ID
			let idStr;
			if (typeof rawId === "object" && rawId !== null) {
				if (rawId.common !== undefined) idStr = `c.${rawId.common}`;
				else {
					const vals = Object.values(rawId);
					idStr = vals.length === 1 ? String(vals[0]) : String(rawId);
				}
			} else if (rawId != null) {
				idStr = String(rawId);
			}

			// The common filter ID is what items use as keys in item.filters
			const commonStr = commonId != null ? String(commonId) : null;

			if (commonStr) {
				// Primary mappings: reference ↔ commonStr (most important for filterIndex lookup)
				referenceMap[reference] = commonStr;
				referenceMap[commonStr] = reference;
				// Also map definition ID → commonStr so any lookup path resolves
				if (idStr && idStr !== commonStr) {
					referenceMap[idStr] = commonStr;
				}
			} else if (idStr && idStr !== reference) {
				// No common ID available — fall back to reference ↔ idStr
				referenceMap[idStr] = reference;
				referenceMap[reference] = idStr;
			}
		}

		if (Object.keys(referenceMap).length > 0) {
			await this.#queueWrite("cache", "put", {
				key: "offline:filterReferenceMap",
				value: referenceMap,
				cached_on: Date.now(),
			});
		}
	}

	/**
	 * Build the filter referenceMap directly from meta_filters (from /items/meta).
	 * Each meta filter has { id, reference, common, label, type }.
	 * Maps reference ↔ common (the key used in item.filters).
	 */
	async BuildReferenceMapFromMeta(metaFilters) {
		if (!Array.isArray(metaFilters) || metaFilters.length === 0) return;

		const existingMap = await this.GetFilterReferenceMap() || {};
		const referenceMap = { ...existingMap };

		for (const mf of metaFilters) {
			const reference = mf.reference;
			const commonId = mf.common;
			const defId = mf.id;
			if (!reference || commonId == null) continue;

			const commonStr = String(commonId);
			referenceMap[reference] = commonStr;
			referenceMap[commonStr] = reference;
			if (defId != null) {
				const idStr = String(defId);
				if (idStr !== commonStr) {
					referenceMap[idStr] = commonStr;
				}
			}
		}

		await this.#queueWrite("cache", "put", {
			key: "offline:filterReferenceMap",
			value: referenceMap,
			cached_on: Date.now(),
		});
	}

	GetCachedFilters() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get("offline:filters");

				request.onsuccess = () => resolve(request.result?.value || null);
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetCachedFilters", e);
				resolve(null);
			});
		});
	}

	async CacheFieldDefs(fieldDefs) {
		if (!fieldDefs) return;

		let normalized;

		// Handle both formats:
		// - New format: { thumb: [...], details: [...] }
		// - Old format (from precache): [ { context: "thumb", ...}, { context: "detail", ...} ]
		if (Array.isArray(fieldDefs)) {
			// Convert old flat array format to new structure
			normalized = {
				thumb: fieldDefs.filter(def => def.context === "thumb"),
				details: fieldDefs.filter(def => def.context === "detail"),
			};
		} else if (typeof fieldDefs === 'object') {
			normalized = fieldDefs;
		} else {
			return;
		}

		let sanitized;
		try {
			sanitized = JSON.parse(JSON.stringify(normalized));
		} catch (e) {
			console.warn("[OfflineStore] Could not serialize field defs:", e);
			return;
		}

		await this.#queueWrite("cache", "put", {
			key: "offline:fieldDefs",
			value: sanitized,
			cached_on: Date.now(),
		});
	}

	GetCachedFieldDefs() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get("offline:fieldDefs");

				request.onsuccess = () => resolve(request.result?.value || null);
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetCachedFieldDefs", e);
				resolve(null);
			});
		});
	}

	async GetFieldDefsByContext(context) {
		const fieldDefs = await this.GetCachedFieldDefs();
		if (!fieldDefs) return [];

		// New structure: { thumb: [...], details: [...] }
		if (context === "thumb") {
			return fieldDefs.thumb || [];
		}
		if (context === "detail") {
			return fieldDefs.details || [];
		}

		return [];
	}

	// ─── On-Demand Data (Prices, Inventory) ─────────────────────────

	/**
	 * Cache on-demand data for a given type (e.g. "prices", "inventory").
	 * Stores as a single object keyed by item ID.
	 * @param {string} type - "prices" or "inventory"
	 * @param {Object} data - { itemId: { field: value, ... }, ... }
	 */
	async CacheOnDemandData(type, data) {
		if (!type || !data) return;

		let sanitized;
		try {
			sanitized = JSON.parse(JSON.stringify(data));
		} catch (e) {
			console.warn(`[OfflineStore] Could not serialize on-demand ${type} data:`, e);
			return;
		}

		await this.#queueWrite("cache", "put", {
			key: `offline:${type}`,
			value: sanitized,
			cached_on: Date.now(),
		});
	}

	/**
	 * Retrieve cached on-demand data for a given type.
	 * @param {string} type - "prices" or "inventory"
	 * @returns {Promise<Object|null>} { itemId: { field: value, ... }, ... }
	 */
	GetCachedOnDemandData(type) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get(`offline:${type}`);

				request.onsuccess = () => resolve(request.result?.value || null);
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log(`GetCachedOnDemandData:${type}`, e);
				resolve(null);
			});
		});
	}

	/**
	 * Merge new entries into existing on-demand data cache.
	 * @param {string} type - "prices" or "inventory"
	 * @param {Object} newEntries - { itemId: { field: value, ... }, ... }
	 */
	async MergeOnDemandData(type, newEntries) {
		if (!type || !newEntries || typeof newEntries !== 'object') return;

		const existing = await this.GetCachedOnDemandData(type) || {};
		const merged = { ...existing, ...newEntries };
		await this.CacheOnDemandData(type, merged);
	}

	GetFilterReferenceMap() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get("offline:filterReferenceMap");

				request.onsuccess = () => resolve(request.result?.value || null);
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetFilterReferenceMap", e);
				resolve(null);
			});
		});
	}

	async SetSyncMetadata(key, value) {
		await this.#queueWrite("cache", "put", {
			key: `sync:${key}`,
			value,
			cached_on: Date.now(),
		});
	}

	GetSyncMetadata(key) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get(`sync:${key}`);

				request.onsuccess = () => resolve(request.result?.value || null);
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetSyncMetadata", e, { key });
				resolve(null);
			});
		});
	}

	async SaveSyncProgress(progress) {
		await this.#queueWrite("cache", "put", {
			key: "sync:progress",
			value: {
				...progress,
				updatedAt: Date.now(),
			},
			cached_on: Date.now(),
		});
	}

	GetSyncProgress() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get("sync:progress");

				request.onsuccess = () => resolve(request.result?.value || null);
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetSyncProgress", e);
				resolve(null);
			});
		});
	}

	async ClearSyncProgress() {
		const db = await getPooledConnection(this.#dbName);
		if (!db || !db.objectStoreNames.contains("cache")) return;

		const tx = db.transaction(["cache"], "readwrite");
		const store = tx.objectStore("cache");
		store.delete("sync:progress");
	}

	async HasIncompleteSyncProgress() {
		const progress = await this.GetSyncProgress();
		if (!progress) return false;
		// Sync is incomplete if phase is not 'complete'
		// This covers all phases: data, indexing, images, assets
		// The old check (downloaded < total) only detected incomplete data phase,
		// missing cases where items were done but images/assets were not
		return progress.phase && progress.phase !== 'complete';
	}

	async CacheTermDictionary(terms) {
		const termArray = Array.isArray(terms) ? terms : Array.from(terms);

		await this.#queueWrite("cache", "put", {
			key: "offline:termDictionary",
			value: termArray,
			cached_on: Date.now(),
		});
	}

	GetTermDictionary() {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get("offline:termDictionary");

				request.onsuccess = () => resolve(request.result?.value || null);
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetTermDictionary", e);
				resolve(null);
			});
		});
	}

	async FindClosestTerm(query, maxDistance = 2) {
		const terms = await this.GetTermDictionary();
		if (!terms || terms.length === 0) return null;

		const queryLower = query.toLowerCase().trim();
		if (queryLower.length < 3) return null;

		if (terms.includes(queryLower)) return null;

		let bestMatch = null;
		let bestDistance = Infinity;

		for (const term of terms) {
			if (Math.abs(term.length - queryLower.length) > maxDistance) continue;

			const distance = levenshteinDistance(queryLower, term, bestDistance - 1);

			if (distance < bestDistance && distance <= maxDistance && distance > 0) {
				bestDistance = distance;
				bestMatch = term;
			}
		}

		return bestMatch;
	}

	async RemovePrecacheItems(itemIds) {
		if (!itemIds || itemIds.length === 0) return;

		const db = await getPooledConnection(this.#dbName);
		if (!db || !db.objectStoreNames.contains("items")) return;

		const tx = db.transaction(["items"], "readwrite");
		const store = tx.objectStore("items");

		for (const itemId of itemIds) {
			const key = `precache:${itemId}`;
			store.delete(key);
		}

		await new Promise((resolve) => {
			tx.oncomplete = resolve;
			tx.onerror = () => resolve();
		});
	}

	async ClearPrecacheData() {
		const db = await getPooledConnection(this.#dbName);
		if (!db) return;

		if (db.objectStoreNames.contains("items")) {
			const tx = db.transaction(["items"], "readwrite");
			const store = tx.objectStore("items");
			const index = store.index("class_name");
			const request = index.openCursor(IDBKeyRange.only("PrecacheItem"));

			request.onsuccess = (event) => {
				const cursor = event.target.result;
				if (cursor) {
					cursor.delete();
					cursor.continue();
				}
			};

			await new Promise(resolve => {
				tx.oncomplete = resolve;
				tx.onerror = () => resolve();
			});
		}

		if (db.objectStoreNames.contains("cache")) {
			const tx = db.transaction(["cache"], "readwrite");
			const store = tx.objectStore("cache");

			store.delete("offline:filters");
			store.delete("offline:fieldDefs");
			store.delete("offline:filterReferenceMap");
			store.delete("offline:termDictionary");
			store.delete("sync:lastSyncTime");
			store.delete("sync:pages");
			store.delete("sync:cacheVersion");

			const cursorRequest = store.openCursor();
			cursorRequest.onsuccess = (event) => {
				const cursor = event.target.result;
				if (cursor) {
					if (typeof cursor.key === "string" && (
						cursor.key.startsWith("page:")
						|| cursor.key.startsWith("sync:precacheIndex:")
					)) {
						cursor.delete();
					}
					cursor.continue();
				}
			};

			await new Promise(resolve => {
				tx.oncomplete = resolve;
				tx.onerror = () => resolve();
			});
		}
	}

	// ─── Page-to-Item Mappings ─────────────────────────────────

	async CachePageItemIds(pageKey, itemIds, metadata = {}) {
		if (!pageKey || !Array.isArray(itemIds)) return;

		await this.#queueWrite("cache", "put", {
			key: `page:${pageKey}`,
			value: {
				itemIds,
				total: metadata.total || itemIds.length,
				filters: metadata.filters || null,
				search: metadata.search || null,
				sort: metadata.sort || null,
			},
			cached_on: Date.now(),
		});
	}

	GetPageItemIds(pageKey) {
		return new Promise((resolve) => {
			getPooledConnection(this.#dbName).then(db => {
				if (!db) return resolve(null);
				if (!db.objectStoreNames.contains("cache")) return resolve(null);

				const tx = db.transaction(["cache"], "readonly");
				const store = tx.objectStore("cache");
				const request = store.get(`page:${pageKey}`);

				request.onsuccess = () => {
					const record = request.result;
					if (!record) return resolve(null);
					resolve({
						...record.value,
						cachedOn: record.cached_on,
					});
				};
				request.onerror = () => resolve(null);
			}).catch((e) => {
				OfflineErrors.log("GetPageItemIds", e, { pageKey });
				resolve(null);
			});
		});
	}

	async GetPageItems(pageKey, limit = 60, offset = 0) {
		const pageData = await this.GetPageItemIds(pageKey);
		if (!pageData || !pageData.itemIds) return null;

		const { itemIds, total, cachedOn } = pageData;

		const paginatedIds = itemIds.slice(offset, offset + limit);

		const items = await this.GetPrecachedItemsByIds(paginatedIds);

		const itemsMap = new Map(items.map(item => [item.attribs?.id, item]));
		const orderedItems = paginatedIds
			.map(id => itemsMap.get(id) || itemsMap.get(String(id)) || itemsMap.get(Number(id)))
			.filter(Boolean);

		return {
			items: orderedItems,
			total,
			cachedOn,
		};
	}

	async ClearPageMappings() {
		const db = await getPooledConnection(this.#dbName);
		if (!db || !db.objectStoreNames.contains("cache")) return;

		const tx = db.transaction(["cache"], "readwrite");
		const store = tx.objectStore("cache");
		const request = store.openCursor();

		request.onsuccess = (event) => {
			const cursor = event.target.result;
			if (cursor) {
				if (cursor.key.startsWith("page:")) {
					cursor.delete();
				}
				cursor.continue();
			}
		};

		await new Promise(resolve => {
			tx.oncomplete = resolve;
			tx.onerror = () => resolve();
		});
	}

	// ─── Client-Side Filtering (fast path via PrecacheIndex) ─────

	async #filterViaIndex(index, filters, search, limit, offset, sort, group, pageId) {
		// If filters are active but the filter index is empty, bail out so the caller
		// falls through to the API (which can actually apply the filters server-side).
		const hasActiveFilters = Array.isArray(filters) && filters.some(f => f?.value != null && (!Array.isArray(f.value) || f.value.length > 0));
		if (hasActiveFilters && !(await index.hasFilterData())) {
			throw new Error("Filter index is empty — cannot apply filters from precache");
		}

		const referenceMap = await this.GetFilterReferenceMap() || {};

		// Step 1: Get candidate IDs from filters + page scope (via inverted index)
		let candidateIds = await index.getMatchingItemIds(filters, pageId, referenceMap);

		// Step 2: Apply search (via word-prefix index)
		let orderedIds;
		if (search && search.trim()) {
			const searchResults = await index.searchItems(search, candidateIds);
			// searchResults is already sorted by relevance
			orderedIds = searchResults || [];
		} else {
			// Convert Set to array for sorting
			if (candidateIds) {
				orderedIds = [...candidateIds];
			} else {
				// No filters/page — get all item IDs from metadata
				orderedIds = await index.getAllItemIds() || [];
			}
		}

		// Step 3: Apply grouping via pre-built group index
		let groupCounts = null;
		let groupPriceRanges = null;
		let groupFilteredMembers = null;
		if (group && group !== '' && group !== 'common_sku') {
			const hasSearch = !!(search && search.trim());
			const groupResult = await index.applyGrouping(orderedIds, hasSearch);
			if (groupResult) {
				orderedIds = groupResult.ids;
				groupCounts = groupResult.groupCounts;
				groupPriceRanges = groupResult.groupPriceRanges;
				groupFilteredMembers = groupResult.groupFilteredMembers;
			}
		}

		// Step 4: Sort (skip if search — search results are already ranked by relevance)
		if (!(search && search.trim())) {
			orderedIds = await index.sortItemIds(orderedIds, sort);
		}

		const total = orderedIds.length;

		// Step 5: Paginate IDs, then load only the needed items from IndexedDB
		const paginatedIds = orderedIds.slice(offset, offset + limit);
		const items = await this.GetPrecachedItemsByIds(paginatedIds);

		// Stale index detection: if IDB returned far fewer items than requested,
		// the persisted index is out of sync with IDB — fall through to API
		if (paginatedIds.length > 0 && items.length < paginatedIds.length * 0.5) {
			console.warn("[#filterViaIndex] stale index detected —", items.length, "items from IDB for", paginatedIds.length, "IDs. Falling through to API.");
			return null;
		}

		// Preserve order from paginatedIds
		const itemMap = new Map(items.map(item => [item.attribs?.id, item]));
		const orderedItems = paginatedIds
			.map(id => itemMap.get(id) || itemMap.get(Number(id)) || itemMap.get(String(id)))
			.filter(Boolean);

		// Attach group member counts, price ranges, and filtered variations
		if (groupCounts) {
			for (const item of orderedItems) {
				const itemId = item.attribs?.id;
				const count = groupCounts[itemId];
				if (count && count > 1) item.count = count;
				if (!item.attribs) item.attribs = {};
				if (groupPriceRanges?.[itemId] != null) {
					// Multi-member filtered group — use computed range
					item.attribs.retail_price_range = groupPriceRanges[itemId];
				} else if (hasActiveFilters) {
					// Single filtered member — clear any full-family range from sync
					// so #transformPrecacheItem falls back to the individual retail_price
					delete item.attribs.retail_price_range;
				}
				// When filters are active, attach filtered member IDs as variations
				// so ProductCard only shows swatches for matching items, not the entire family
				if (hasActiveFilters && groupFilteredMembers?.[itemId]) {
					item.variations = groupFilteredMembers[itemId];
				}
			}
		}

		// Step 6: Spelling suggestion (reuse existing term dictionary)
		let suggestion = null;
		if (search && search.trim()) {
			const searchTerms = search.toLowerCase().trim().split(/[\s\n]+/).filter(t => t.length >= 3);
			const terms = await this.GetTermDictionary();
			const termsSet = terms ? new Set(terms) : null;

			if (termsSet && searchTerms.length > 0) {
				const correctedTerms = [];
				let hasCorrectedTerm = false;

				for (const term of searchTerms) {
					if (termsSet.has(term)) {
						correctedTerms.push(term);
					} else {
						const correction = await this.FindClosestTerm(term);
						if (correction) {
							correctedTerms.push(correction);
							hasCorrectedTerm = true;
						} else {
							correctedTerms.push(term);
						}
					}
				}

				if (hasCorrectedTerm) {
					suggestion = correctedTerms.join(' ');
				}
			}
		}

		return { items: orderedItems, total, suggestion };
	}

	// ─── Client-Side Filtering (brute-force fallback) ─────────────

	async FilterPrecachedItems(filters = [], search = "", limit = 60, offset = 0, sort = null, group = null, pageId = null) {
		// If filters are active but precache has no filter data, return null to signal
		// the caller to fall through to the API (which can apply filters server-side).
		const hasActiveFilters = Array.isArray(filters) && filters.some(f => f?.value != null && (!Array.isArray(f.value) || f.value.length > 0));
		const precacheIndex = PrecacheIndex.ForCatalog(this.#catalogId);
		if (hasActiveFilters && !(await precacheIndex.hasFilterData())) {
			return null;
		}

		// Try PrecacheIndex fast path first (inverted indexes, loads only needed items)
		const indexReady = await precacheIndex.isReady();
		if (indexReady) {
			try {
				const indexResult = await this.#filterViaIndex(precacheIndex, filters, search, limit, offset, sort, group, pageId);
				return indexResult;
			} catch (e) {
				console.warn("[OfflineStore] PrecacheIndex fast path failed, falling back to brute force:", e);
				OfflineErrors.log("FilterPrecachedItems", e, { path: "PrecacheIndex fast path" });
			}
		}

		// Fast path: page-scoped query with no search/filters/grouping — load only page items by ID
		const hasFilters = Array.isArray(filters) && filters.some(f => f?.value != null && (!Array.isArray(f.value) || f.value.length > 0));
		const hasSearch = !!(search && search.trim());
		const hasGroup = !!(group && group !== '');
		if (pageId != null && !hasFilters && !hasSearch && !hasGroup) {
			const cachedItemIds = await this.GetSyncMetadata(`pageItems:${pageId}`);
			if (cachedItemIds && Array.isArray(cachedItemIds)) {
				const total = cachedItemIds.length;
				const paginatedIds = cachedItemIds.slice(offset, offset + limit);
				const items = await this.GetPrecachedItemsByIds(paginatedIds);
				// Preserve page ordering
				const idMap = new Map(items.map(item => [Number(item.attribs?.id), item]));
				const ordered = paginatedIds.map(id => idMap.get(Number(id))).filter(Boolean);
				return { items: ordered, total, suggestion: null };
			}
			return { items: [], total: 0 };
		}

		// Legacy brute-force fallback: loads all items into memory
		// Guard against OOM for large catalogs — brute force is only safe for <50k items
		const itemCount = await this.GetPrecacheItemCount();
		if (itemCount > 50000) {
			console.error(`[OfflineStore] ${itemCount} items too large for brute-force filter, returning empty`);
			OfflineErrors.log("FilterPrecachedItems", "Brute-force guard: too many items", { itemCount });
			return { items: [], total: 0, suggestion: null };
		}
		const { items: allItems } = await this.GetPrecachedItems();

		let filtered = allItems;

		// Apply page filtering first
		if (pageId !== null && pageId !== undefined) {
			const cachedItemIds = await this.GetSyncMetadata(`pageItems:${pageId}`);

			if (cachedItemIds && Array.isArray(cachedItemIds)) {
				const pageItemIds = new Set(cachedItemIds.map(id => Number(id)));
				const pageOrder = new Map(cachedItemIds.map((id, idx) => [Number(id), idx]));

				filtered = filtered.filter(item => {
					const itemId = item.attribs?.id;
					if (!itemId) return false;
					return pageItemIds.has(Number(itemId));
				});

				filtered.sort((a, b) => {
					const posA = pageOrder.get(Number(a.attribs?.id)) ?? Infinity;
					const posB = pageOrder.get(Number(b.attribs?.id)) ?? Infinity;
					return posA - posB;
				});
			} else {
				return { items: [], total: 0 };
			}
		}

		// Apply search
		if (search && search.trim()) {
			const searchTerms = search.toLowerCase().trim()
				.split(/[\s\n]+/)
				.filter(term => term.length > 0);

			const scoredItems = filtered.map(item => {
				const attribs = item.attribs || {};

				const searchableFields = [
					attribs.name,
					attribs.sku,
					attribs.common_sku,
					attribs.handle,
					attribs.description,
					attribs.title,
					attribs.keywords,
					attribs.search_terms,
					attribs.tags,
					attribs.category,
					attribs.family,
					attribs.style,
				].filter(Boolean);

				const searchableText = searchableFields
					.map(f => String(f).toLowerCase())
					.join(' ');

				let score = 0;
				let matchedTerms = [];
				const nameText = [attribs.name, attribs.title].filter(Boolean).join(' ').toLowerCase();

				for (const term of searchTerms) {
					if (searchableText.includes(term)) {
						score++;
						matchedTerms.push(term);
						if (searchableText.match(new RegExp(`\\b${term}\\b`))) {
							score += 0.5;
						}
						if (nameText.includes(term)) {
							score += 1;
						}
					}
				}

				// Bonus for matching ALL search terms - this ensures multi-word matches rank higher
				if (searchTerms.length > 1 && matchedTerms.length === searchTerms.length) {
					score += 10 * searchTerms.length;
				}

				return { item, score, matchedTerms };
			});

			filtered = scoredItems
				.filter(({ score }) => score > 0)
				.sort((a, b) => b.score - a.score)
				.map(({ item }) => item);
		}

		// Apply filters
		if (filters && filters.length > 0) {
			const sampleItem = allItems[0];
			const sampleItemFilters = sampleItem?.filters;
			const isArrayFormat = Array.isArray(sampleItemFilters);

			// Helper: check if a value string is a range (e.g., "100:200")
			const isRange = (v) => typeof v === 'string' && v.includes(':');

			// Build a map of filter property -> filter definition for range resolution
			const cachedFilterDefs = await this.GetCachedFilters() || [];
			const filterDefMap = new Map();
			for (const fd of cachedFilterDefs) {
				const ref = fd.reference || fd.property || fd.id;
				if (ref) filterDefMap.set(String(ref), fd);
				if (fd.id) filterDefMap.set(String(fd.id), fd);
			}

			// Helper: resolve item's numeric value for a filter property
			const resolveItemValue = (item, filterProperty) => {
				const attribs = item.attribs || item;
				const detailFields = attribs.detail_fields || item.detail_fields || {};
				// Try direct key lookup
				const direct = attribs[filterProperty] ?? detailFields[filterProperty];
				if (direct != null) return direct;
				// Infer from filter label
				const filterDef = filterDefMap.get(String(filterProperty));
				if (filterDef) {
					const label = (filterDef.label || '').toLowerCase().replace(/\s*range\s*/g, '').trim();
					const attrKey = label.replace(/\s+/g, '_');
					const v = attribs[attrKey] ?? detailFields[attrKey];
					if (v != null) return v;
					// Try common price fields if label suggests price
					if (label.includes('price') || label.includes('cost')) {
						for (const pk of ['retail_price', 'price', 'wholesale_price', 'cost', 'unit_price']) {
							const pv = attribs[pk] ?? detailFields[pk];
							if (pv != null) return pv;
						}
					}
				}
				return undefined;
			};

			// Helper: check if an item's attribute falls within a range
			const matchesRange = (item, rangeStr, filterProperty) => {
				const parts = rangeStr.split(':');
				const min = parseFloat(parts[0]);
				const max = parts[1] ? parseFloat(parts[1]) : Infinity;
				if (isNaN(min)) return false;
				const rawVal = resolveItemValue(item, filterProperty);
				if (rawVal == null) return false;
				const val = parseFloat(String(rawVal).replace(/[^0-9.-]/g, ''));
				return !isNaN(val) && val >= min && val <= max;
			};

			if (isArrayFormat) {
				filtered = filtered.filter(item => {
					const itemFilterIds = item.filters || [];

					return filters.every(filter => {
						const { value, filterId, property } = filter;
						const filterProp = filterId || property;

						if (Array.isArray(value)) {
							if (value.length === 0) return true;
							// Check for range values in the array
							const rangeValues = value.filter(isRange);
							const idValues = value.filter(v => !isRange(v));

							const idMatch = idValues.length > 0 && idValues.some(v =>
								itemFilterIds.includes(v) ||
								itemFilterIds.includes(String(v)) ||
								itemFilterIds.includes(Number(v))
							);

							// Range IDs (e.g. "100000:10000000") can appear directly in item.filters
							const rangeIdMatch = rangeValues.length > 0 && rangeValues.some(r =>
								itemFilterIds.includes(r) || itemFilterIds.includes(String(r))
							);

							// Fall back to numeric attribute comparison for ranges
							const rangeMatch = !rangeIdMatch && rangeValues.length > 0
								&& rangeValues.some(r => matchesRange(item, r, filterProp));

							return idMatch || rangeIdMatch || rangeMatch;
						}

						if (typeof value === "boolean") {
							return true;
						}

						if (isRange(value)) {
							// Check direct ID match first
							if (itemFilterIds.includes(value) || itemFilterIds.includes(String(value))) {
								return true;
							}
							return matchesRange(item, value, filterProp);
						}

						return itemFilterIds.includes(value) ||
							itemFilterIds.includes(String(value)) ||
							itemFilterIds.includes(Number(value));
					});
				});
			} else {
				const referenceMap = await this.GetFilterReferenceMap() || {};

				filtered = filtered.filter(item => {
					const itemFilters = item.filters || {};
					return filters.every(filter => {
						let { filterId, value } = filter;

						// Try the original filterId first, then referenceMap, then prefix match
						let itemValue = itemFilters[filterId];
						if (itemValue === undefined) {
							const mappedId = referenceMap[filterId];
							if (mappedId && itemFilters[mappedId] !== undefined) {
								filterId = mappedId;
								itemValue = itemFilters[filterId];
							}
						}

						if (Array.isArray(value)) {
							if (value.length === 0) return true;
							// Check for range values
							const rangeValues = value.filter(isRange);
							const idValues = value.filter(v => !isRange(v));

							let idMatch = false;
							if (idValues.length > 0) {
								if (Array.isArray(itemValue)) {
									idMatch = itemValue.some(v => idValues.some(iv => String(iv) === String(v)));
								} else if (itemValue != null) {
									idMatch = idValues.some(iv => String(iv) === String(itemValue));
								}
							}
							const rangeMatch = rangeValues.length > 0 && rangeValues.some(r => matchesRange(item, r, filterId));
							return idMatch || rangeMatch;
						}

						if (typeof value === "boolean") {
							return value ? Boolean(itemValue) : true;
						}

						if (isRange(value)) {
							return matchesRange(item, value, filterId);
						}

						return itemValue != null && String(itemValue) === String(value);
					});
				});
			}
		}

		// Apply sorting
		if (sort) {
			const [field, direction] = sort.includes(":") ? sort.split(":") : [sort, "asc"];
			const dir = direction === "desc" ? -1 : 1;
			filtered.sort((a, b) => {
				const aVal = a.attribs?.[field];
				const bVal = b.attribs?.[field];
				if (aVal == null && bVal == null) return 0;
				if (aVal == null) return dir;
				if (bVal == null) return -dir;
				if (typeof aVal === "string") return dir * aVal.localeCompare(bVal);
				return dir * (aVal - bVal);
			});
		}

		// Apply grouping
		if (group && group !== '' && group !== 'common_sku') {
			const groupMap = new Map();
			const groupCountMap = new Map();
			const groupPriceMap = new Map(); // groupCode -> { min, max }
			const sizeStripRegex = /\s+(SS|S\d+|XS|XXL|XL|Small|Medium|Large|S|M|L|\d+(\.\d+)?)$/i;

			for (const item of filtered) {
				let groupCode = item.group || item.attribs?.grouping || item.attribs?.family;

				if (!groupCode && item.attribs?.common_sku) {
					groupCode = item.attribs.common_sku;
				}

				if (groupCode) {
					groupCode = String(groupCode).replace(sizeStripRegex, '').trim();
				}

				if (!groupCode) {
					const itemId = item.attribs?.id || item.key || ('_idx_' + filtered.indexOf(item));
					if (!groupMap.has(`_ungrouped_${itemId}`)) {
						groupMap.set(`_ungrouped_${itemId}`, item);
					}
					continue;
				}

				// Track group member counts
				groupCountMap.set(groupCode, (groupCountMap.get(groupCode) || 0) + 1);

				// Track price range per group
				const price = parseFloat(item.attribs?.retail_price || item.attribs?.detail_fields?.retail_price);
				if (price > 0) {
					const existing = groupPriceMap.get(groupCode);
					if (!existing) {
						groupPriceMap.set(groupCode, { min: price, max: price });
					} else {
						if (price < existing.min) existing.min = price;
						if (price > existing.max) existing.max = price;
					}
				}

				const existingRep = groupMap.get(groupCode);
				if (!existingRep) {
					groupMap.set(groupCode, item);
				} else if (!(search && search.trim())) {
					// When NOT searching, select representative by lowest group_order.
					// When searching, the first seen item has the highest relevance score,
					// so we keep it as the representative (don't replace).
					const existingOrder = existingRep.group_order ?? Infinity;
					const currentOrder = item.group_order ?? Infinity;

					if (currentOrder < existingOrder) {
						groupMap.set(groupCode, item);
					} else if (currentOrder === existingOrder) {
						// Same group_order - use id as tiebreaker
						const existingId = existingRep.attribs?.id ?? Infinity;
						const currentId = item.attribs?.id ?? Infinity;
						if (currentId < existingId) {
							groupMap.set(groupCode, item);
						}
					}
				}
			}

			// Attach group member counts and price ranges
			for (const [groupCode, item] of groupMap.entries()) {
				if (!groupCode.startsWith('_ungrouped_')) {
					const count = groupCountMap.get(groupCode) || 1;
					if (count > 1) item.count = count;
					const priceRange = groupPriceMap.get(groupCode);
					if (priceRange) {
						if (!item.attribs) item.attribs = {};
						item.attribs.retail_price_range = priceRange.min === priceRange.max
							? priceRange.min
							: `${priceRange.min} - ${priceRange.max}`;
					}
				}
			}

			filtered = Array.from(groupMap.values());

			if (sort) {
				const [field, direction] = sort.includes(":") ? sort.split(":") : [sort, "asc"];
				const dir = direction === "desc" ? -1 : 1;
				filtered.sort((a, b) => {
					const aVal = a.attribs?.[field];
					const bVal = b.attribs?.[field];
					if (aVal == null && bVal == null) return 0;
					if (aVal == null) return dir;
					if (bVal == null) return -dir;
					if (typeof aVal === "string") return dir * aVal.localeCompare(bVal);
					return dir * (aVal - bVal);
				});
			} else if (!(search && search.trim())) {
				// Only re-sort by sequence when there's no search active.
				// When search is active, the array is already sorted by relevance
				// and groupMap.values() preserves that insertion order.
				filtered.sort((a, b) => {
					const seqA = a.attribs?.sequence || 0;
					const seqB = b.attribs?.sequence || 0;
					if (seqA > 0 && seqB > 0) return seqA - seqB;
					if (seqA > 0 && seqB === 0) return -1;
					if (seqA === 0 && seqB > 0) return 1;
					return (a.attribs?.id || 0) - (b.attribs?.id || 0);
				});
			}
		}

		const total = filtered.length;
		const paginated = filtered.slice(offset, offset + limit);

		// Check for misspelled terms
		let suggestion = null;
		if (search && search.trim()) {
			const searchTerms = search.toLowerCase().trim().split(/[\s\n]+/).filter(t => t.length >= 3);
			const terms = await this.GetTermDictionary();
			const termsSet = terms ? new Set(terms) : null;

			if (termsSet && searchTerms.length > 0) {
				const correctedTerms = [];
				let hasCorrectedTerm = false;

				for (const term of searchTerms) {
					if (termsSet.has(term)) {
						correctedTerms.push(term);
					} else {
						const correction = await this.FindClosestTerm(term);
						if (correction) {
							correctedTerms.push(correction);
							hasCorrectedTerm = true;
						} else {
							correctedTerms.push(term);
						}
					}
				}

				if (hasCorrectedTerm) {
					suggestion = correctedTerms.join(' ');
				}
			}
		}

		return { items: paginated, total, suggestion };
	}
}


// ═══════════════════════════════════════════════════════════════════
// OfflineStore — static shell with ForCatalog factory + bridge methods
// ═══════════════════════════════════════════════════════════════════

/**
 * Offline storage layer for cached API responses and pending mutations.
 *
 * Usage (preferred):
 *   const store = OfflineStore.ForCatalog(catalogId);
 *   const items = await store.GetPrecachedItems();
 *
 * Legacy usage (bridge — still works):
 *   OfflineStore.SetCatalog(catalogId);
 *   const items = await OfflineStore.GetPrecachedItems();
 */
export default class OfflineStore {
	static #instances = new Map();
	static #defaultCatalogId = null;

	/**
	 * Get a catalog-bound store instance.
	 * Instances are cached — same catalogId always returns the same instance.
	 * @param {string|number} catalogId
	 * @returns {OfflineStoreInstance}
	 */
	static ForCatalog(catalogId) {
		const key = String(catalogId);
		if (!this.#instances.has(key)) {
			this.#instances.set(key, new OfflineStoreInstance(catalogId));
		}
		return this.#instances.get(key);
	}

	/**
	 * Set the default catalog for legacy static method calls.
	 * Prefer using ForCatalog() instead.
	 * @param {string|number} catalogId
	 */
	static SetCatalog(catalogId) {
		this.#defaultCatalogId = catalogId;
	}

	/** @returns {OfflineStoreInstance|null} */
	static #default() {
		if (!this.#defaultCatalogId) return null;
		return this.ForCatalog(this.#defaultCatalogId);
	}

	// ─── Catalog-Independent Methods (no bridge needed) ─────────

	static IsOffline() {
		const simulateOffline = localStorage.getItem("simulate-offline") === "true";
		return !navigator.onLine || simulateOffline;
	}

	static IsSimulatingOffline() {
		return localStorage.getItem("simulate-offline") === "true";
	}

	static GenerateKey(method, url, params) {
		const normalized = params
			? JSON.stringify(params, Object.keys(params).sort())
			: "";
		return `${method}:${url}:${normalized}`;
	}

	static GeneratePageKey(basePath, params = {}) {
		const normalized = {
			filters: params.filters ? JSON.stringify(params.filters) : "",
			search: params.search || "",
			sort: params.sort || "",
			page: params.page || 1,
		};
		return `${basePath}:${JSON.stringify(normalized)}`;
	}

	static async GetStorageStats() {
		try {
			if (!navigator.storage?.estimate) {
				return { usage: 0, quota: 0, percent: 0 };
			}
			const { usage, quota } = await navigator.storage.estimate();
			return {
				usage: usage || 0,
				quota: quota || 0,
				percent: quota ? Math.round((usage / quota) * 100) : 0,
			};
		} catch (e) {
			return { usage: 0, quota: 0, percent: 0 };
		}
	}

	// ─── Bridge Methods (delegate to default instance) ──────────

	static GetCurrentCatalogId() {
		return this.#default()?.GetCurrentCatalogId() || null;
	}

	// Delta Updates
	static GetLastSyncTime(className) { return this.#default()?.GetLastSyncTime(className) || null; }
	static SetLastSyncTime(className) { this.#default()?.SetLastSyncTime(className); }
	static GetDeltaParams(className) { return this.#default()?.GetDeltaParams(className) || null; }

	// Response Cache
	static async CacheResponse(key, className, responseData) { return this.#default()?.CacheResponse(key, className, responseData); }
	static GetCachedResponse(key, ttlMs) { return this.#default()?.GetCachedResponse(key, ttlMs) ?? Promise.resolve(); }
	static GetCachedResponseWithMeta(key, ttlMs) { return this.#default()?.GetCachedResponseWithMeta(key, ttlMs) ?? Promise.resolve(null); }
	static GetCachedGalleryAttributes() { return this.#default()?.GetCachedGalleryAttributes() ?? Promise.resolve(null); }
	static GetCacheSummary() { return this.#default()?.GetCacheSummary() ?? Promise.resolve([]); }
	static async ClearClassCache(className) { return this.#default()?.ClearClassCache(className); }

	// Sync Queue
	static QueueMutation(className, method, url, payload, config) { return this.#default()?.QueueMutation(className, method, url, payload, config) ?? Promise.reject(new Error("No catalog set")); }
	static GetPendingMutations() { return this.#default()?.GetPendingMutations() ?? Promise.resolve([]); }
	static async ClearMutation(id) { return this.#default()?.ClearMutation(id); }
	static async FailMutation(id) { return this.#default()?.FailMutation(id); }
	static GetPendingCount() { return this.#default()?.GetPendingCount() ?? Promise.resolve(0); }
	static async ClearQueue() { return this.#default()?.ClearQueue(); }

	// Item-Level Cache
	static async CacheItems(className, items) { return this.#default()?.CacheItems(className, items); }
	static GetItemsByIds(className, ids) { return this.#default()?.GetItemsByIds(className, ids) ?? Promise.resolve([]); }
	static GetCachedItems(className, limit, offset) { return this.#default()?.GetCachedItems(className, limit, offset) ?? Promise.resolve({ items: [], total: 0 }); }
	static GetItemCount(className) { return this.#default()?.GetItemCount(className) ?? Promise.resolve(0); }

	// Database Management
	static async ResetDatabase() { return this.#default()?.ResetDatabase() ?? false; }

	// Precache / Offline Sync
	static async CachePrecacheItems(items) { return this.#default()?.CachePrecacheItems(items); }
	static GetPrecachedItems(limit, offset) { return this.#default()?.GetPrecachedItems(limit, offset) ?? Promise.resolve({ items: [], total: 0 }); }
	static DebugPrecacheItemIds(targetId) { return this.#default()?.DebugPrecacheItemIds(targetId) ?? Promise.resolve(); }
	static GetPrecacheItemCount() { return this.#default()?.GetPrecacheItemCount() ?? Promise.resolve(0); }
	static GetPrecachedItemById(itemId) { return this.#default()?.GetPrecachedItemById(itemId) ?? Promise.resolve(null); }
	static GetPrecachedItemsByIds(itemIds) { return this.#default()?.GetPrecachedItemsByIds(itemIds) ?? Promise.resolve([]); }
	static GetItemsByGroupCode(groupCode) { return this.#default()?.GetItemsByGroupCode(groupCode) ?? Promise.resolve([]); }

	// Assets
	static async CacheAssets(assets) { return this.#default()?.CacheAssets(assets); }
	static GetCachedAssets() { return this.#default()?.GetCachedAssets() ?? Promise.resolve(null); }

	// Filters & Field Defs
	static async CacheFilters(filters) { return this.#default()?.CacheFilters(filters); }
	static async BuildReferenceMapFromMeta(metaFilters) { return this.#default()?.BuildReferenceMapFromMeta(metaFilters); }
	static GetCachedFilters() { return this.#default()?.GetCachedFilters() ?? Promise.resolve(null); }
	static async CacheFieldDefs(fieldDefs) { return this.#default()?.CacheFieldDefs(fieldDefs); }
	static GetCachedFieldDefs() { return this.#default()?.GetCachedFieldDefs() ?? Promise.resolve(null); }
	static async GetFieldDefsByContext(context) { return this.#default()?.GetFieldDefsByContext(context) ?? []; }
	static GetFilterReferenceMap() { return this.#default()?.GetFilterReferenceMap() ?? Promise.resolve(null); }

	// On-Demand Data (Prices, Inventory)
	static async CacheOnDemandData(type, data) { return this.#default()?.CacheOnDemandData(type, data); }
	static GetCachedOnDemandData(type) { return this.#default()?.GetCachedOnDemandData(type) ?? Promise.resolve(null); }
	static async MergeOnDemandData(type, newEntries) { return this.#default()?.MergeOnDemandData(type, newEntries); }

	// Sync Metadata & Progress
	static async SetSyncMetadata(key, value) { return this.#default()?.SetSyncMetadata(key, value); }
	static GetSyncMetadata(key) { return this.#default()?.GetSyncMetadata(key) ?? Promise.resolve(null); }
	static async SaveSyncProgress(progress) { return this.#default()?.SaveSyncProgress(progress); }
	static GetSyncProgress() { return this.#default()?.GetSyncProgress() ?? Promise.resolve(null); }
	static async ClearSyncProgress() { return this.#default()?.ClearSyncProgress(); }
	static async HasIncompleteSyncProgress() { return this.#default()?.HasIncompleteSyncProgress() ?? false; }

	// Term Dictionary & Search
	static async CacheTermDictionary(terms) { return this.#default()?.CacheTermDictionary(terms); }
	static GetTermDictionary() { return this.#default()?.GetTermDictionary() ?? Promise.resolve(null); }
	static async FindClosestTerm(query, maxDistance) { return this.#default()?.FindClosestTerm(query, maxDistance) ?? null; }

	// Precache Removal
	static async RemovePrecacheItems(itemIds) { return this.#default()?.RemovePrecacheItems(itemIds); }
	static async ClearPrecacheData() { return this.#default()?.ClearPrecacheData(); }

	// Page Mappings
	static async CachePageItemIds(pageKey, itemIds, metadata) { return this.#default()?.CachePageItemIds(pageKey, itemIds, metadata); }
	static GetPageItemIds(pageKey) { return this.#default()?.GetPageItemIds(pageKey) ?? Promise.resolve(null); }
	static async GetPageItems(pageKey, limit, offset) { return this.#default()?.GetPageItems(pageKey, limit, offset) ?? null; }
	static async ClearPageMappings() { return this.#default()?.ClearPageMappings(); }

	// Client-Side Filtering
	static async FilterPrecachedItems(filters, search, limit, offset, sort, group, pageId) { const inst = this.#default(); if (!inst) return { items: [], total: 0 }; return inst.FilterPrecachedItems(filters, search, limit, offset, sort, group, pageId); }
}
